import { CONFIG } from '@/config-global';
import axios, { endpoints } from '@/utils/axios';
import { fetcher } from '@/utils/axios';
import useSWR from 'swr';

const ENDPOINTS = {
  GET_ORGANIZATION: 'get-organization',
  CREATE_ORGANIZATION: 'create-organization',
  JOIN_ORGANIZATION: 'join-organization',
  GET_ORGANIZATION_PROFILE: 'get-organization-profile',
  LIST_ORGANIZATIONS: 'list-organizations',
  GET_ORGANIZATION_BY_ID: 'get-organization-by-id',
  SEARCH_ONBOARDING_ORGANIZATIONS: 'search-onboarding-organizations',
  GET_ONBOARDING_ORGANIZATION: 'get-onboarding-organization',
};

const ORGANIZATION_URL = `${CONFIG.serverUrl}${endpoints.organization}`;

// ----------------------------------------------------------------------

/**
 * Which intake flow an organization runs. bonni-backend resolves it from the
 * organization's ID (never its name) and returns it on list-organizations,
 * get-organization and get-organization-by-id. A backend that predates the
 * field omits it; treat a missing or null value as 'standard'.
 */
export type IntakeProfile = 'standard' | 'linkedin_pilot';

export type Organization = {
  id: string;
  key?: string;
  name: string;
  assistantName?: string | null;
  ownerId: string;
  logoUrl?: string | null;
  brandColor?: string | null;
  secondaryColor?: string | null;
  onboarding?: any;
  intakeProfile?: IntakeProfile | null;
} | null;

export type OrganizationProfile = {
  id: string;
  name: string;
  assistantName?: string | null;
  logoUrl?: string | null;
  brandColor?: string | null;
  secondaryColor?: string | null;
} | null;

// One entry of list-organizations (where currentOrganization comes from), and
// the organization get-organization-by-id returns.
export type OrganizationWithRole = {
  id: string;
  name: string;
  assistantName?: string | null;
  logoUrl?: string | null;
  brandColor?: string | null;
  secondaryColor?: string | null;
  role: string;
  isOwner: boolean;
  intakeProfile?: IntakeProfile | null;
};

export type OnboardingOrganization = {
  id: string;
  name: string;
  assistantName?: string | null;
  logoUrl?: string | null;
};

const swrOptions = {
  revalidateIfStale: false,
  revalidateOnFocus: false,
  revalidateOnReconnect: false,
};

export function useGetOrganization(enabled: boolean = true) {
  const key: any = enabled ? [`${ORGANIZATION_URL}`, { params: { endpoint: ENDPOINTS.GET_ORGANIZATION } }] : null;
  const { data, error, isLoading, isValidating, mutate } = useSWR<{ ok: boolean; organization: Organization }>(
    key,
    fetcher,
    swrOptions
  );

  return { organization: data?.organization || null, loading: isLoading, error, validating: isValidating, refresh: mutate };
}

export function useGetOrganizationProfile(enabled: boolean = true, organizationId?: string | null) {
  const params: Record<string, any> = { endpoint: ENDPOINTS.GET_ORGANIZATION_PROFILE };
  if (organizationId) params.organizationId = organizationId;
  const key: any = enabled ? [`${ORGANIZATION_URL}`, { params }] : null;
  const { data, error, isLoading, isValidating, mutate } = useSWR<{ ok: boolean; organization: OrganizationProfile }>(
    key,
    fetcher,
    swrOptions
  );

  return { organization: data?.organization || null, loading: isLoading, error, validating: isValidating, refresh: mutate };
}

// ----------------------------------------------------------------------
  
type CreateOrgInput = {
  name: string;
  assistantName?: string;
  onboarding?: any;
  organizationId?: string;
};

type JoinOrganizationInput = {
  organizationId: string;
};

export async function createOrganization(payload: CreateOrgInput) {
  const body: any = {
    name: payload.name,
  };
  if (payload.assistantName !== undefined) body.assistantName = payload.assistantName;
  if (payload.onboarding !== undefined) body.onboardingData = payload.onboarding;
  if (payload.organizationId) body.organizationId = payload.organizationId;

  const res = await axios.post(`${endpoints.organization}?endpoint=${ENDPOINTS.CREATE_ORGANIZATION}`, body);
  return res.data;
}

export async function joinOrganization(payload: JoinOrganizationInput) {
  const res = await axios.post(
    `${endpoints.organization}?endpoint=${ENDPOINTS.JOIN_ORGANIZATION}`,
    payload
  );
  return res.data;
}

export function useSearchOnboardingOrganizations(search?: string, limit: number = 50) {
  const params: Record<string, any> = {
    endpoint: ENDPOINTS.SEARCH_ONBOARDING_ORGANIZATIONS,
    limit,
  };

  if (search && search.trim().length > 0) {
    params.q = search.trim();
  }

  const key: any = [`${ORGANIZATION_URL}`, { params }];
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    { ok: boolean; organizations: OnboardingOrganization[] }
  >(key, fetcher, swrOptions);

  return {
    organizations: data?.organizations || [],
    organizationsLoading: isLoading,
    organizationsError: error,
    organizationsValidating: isValidating,
    organizationsRefresh: mutate,
  };
}

export function useGetOnboardingOrganization(organizationId?: string) {
  const params: Record<string, any> = {
    endpoint: ENDPOINTS.GET_ONBOARDING_ORGANIZATION,
  };

  if (organizationId) {
    params.organizationId = organizationId;
  }

  const key: any = organizationId ? [`${ORGANIZATION_URL}`, { params }] : null;
  const { data, error, isLoading, isValidating, mutate } = useSWR<
    { ok: boolean; organization: OnboardingOrganization | null }
  >(key, fetcher, swrOptions);

  return {
    organization: data?.organization || null,
    organizationLoading: isLoading,
    organizationError: error,
    organizationValidating: isValidating,
    organizationRefresh: mutate,
  };
}

export function useGetOrganizations(enabled: boolean = true) {
  const key: any = enabled ? [`${ORGANIZATION_URL}`, { params: { endpoint: ENDPOINTS.LIST_ORGANIZATIONS } }] : null;
  const { data, error, isLoading, isValidating, mutate } = useSWR<{ ok: boolean; organizations: OrganizationWithRole[] }>(
    key,
    fetcher,
    swrOptions
  );

  return { organizations: data?.organizations || [], loading: isLoading, error, validating: isValidating, refresh: mutate };
}

export async function getOrganizationById(
  organizationId: string
): Promise<{ ok: boolean; organization: OrganizationWithRole }> {
  const res = await axios.get(`${endpoints.organization}?endpoint=${ENDPOINTS.GET_ORGANIZATION_BY_ID}&organizationId=${organizationId}`);
  return res.data;
}
